import React, { useState } from "react";
import styled from "styled-components";
import Checkbox from "../../../Generic/Checkbox";
import { Modal } from "../../../Generic/ModalButton";
import { OfferField, OfferMappingField } from "../../constants";
import { Tag, TagList, TwoColumnLayout, Section, SectionTitle, Field, FieldLabel } from "../shared/OfferFormLayout";
import IdEntryModal from "../modals/IdEntryModal";
import MachineSelectModal from "../modals/MachineSelectModal";
import MenuSearchModal from "../modals/MenuSearchModal";
import {
	addMappingId,
	getMappingIds,
	isApplyAll,
	removeMappingId,
	setApplyAll,
} from "../../utils/offerMapping";

const IconButton = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	color: ${(props) => (props.$danger ? "red" : "#36395a")};
	user-select: none;
	flex-shrink: 0;
	line-height: 1;
`;

const LabelRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	width: 100%;
`;

const ApplyAllLabel = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	font-size: 0.85rem;
	font-weight: 500;
	color: #36395a;
	cursor: pointer;
	white-space: nowrap;
`;

const CompactCheckbox = styled(Checkbox)`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
`;

const MappingAndTermsStep = ({ draft, onChange }) => {
	const [menuModalOpen, setMenuModalOpen] = useState(false);
	const [restaurantModalOpen, setRestaurantModalOpen] = useState(false);
	const [userModalOpen, setUserModalOpen] = useState(false);

	const mapping = draft[OfferField.OFFER_MAPPING] || {
		[OfferMappingField.MENU_IDS]: [],
		[OfferMappingField.RESTAURANT_IDS]: [],
		[OfferMappingField.USER_IDS]: [],
	};
	const menuLabels = draft[OfferField.MENU_LABELS] || {};
	const applyAllMenus = isApplyAll(mapping[OfferMappingField.MENU_IDS]);
	const applyAllRestaurants = isApplyAll(mapping[OfferMappingField.RESTAURANT_IDS]);
	const applyAllUsers = isApplyAll(mapping[OfferMappingField.USER_IDS]);
	const menuIds = getMappingIds(mapping[OfferMappingField.MENU_IDS]);
	const restaurantIds = getMappingIds(mapping[OfferMappingField.RESTAURANT_IDS]);
	const userIds = getMappingIds(mapping[OfferMappingField.USER_IDS]);

	function updateMapping(field, value, extra = {}) {
		onChange({ [OfferField.OFFER_MAPPING]: { ...mapping, [field]: value }, ...extra });
	}

	function handleApplyAll(field, checked, clearMenuLabels = false) {
		switch (field) {
			case OfferMappingField.MENU_IDS:
				setMenuModalOpen(false);
				break;
			case OfferMappingField.RESTAURANT_IDS:
				setRestaurantModalOpen(false);
				break;
			case OfferMappingField.USER_IDS:
				setUserModalOpen(false);
				break;
			default:
				break;
		}

		const nextValue = setApplyAll(checked);
		if (clearMenuLabels) {
			updateMapping(field, nextValue, { [OfferField.MENU_LABELS]: {} });
			return;
		}
		updateMapping(field, nextValue);
	}

	function removeId(field, id) {
		updateMapping(field, removeMappingId(mapping[field], id));
	}

	function removeMenuId(id) {
		const nextLabels = { ...menuLabels };
		delete nextLabels[id];
		updateMapping(
			OfferMappingField.MENU_IDS,
			removeMappingId(mapping[OfferMappingField.MENU_IDS], id),
			{ [OfferField.MENU_LABELS]: nextLabels }
		);
	}

	function handleSelectMenu(menu) {
		updateMapping(
			OfferMappingField.MENU_IDS,
			addMappingId(mapping[OfferMappingField.MENU_IDS], menu._id),
			{ [OfferField.MENU_LABELS]: { ...menuLabels, [menu._id]: menu.name } }
		);
		setMenuModalOpen(false);
	}

	function handleAddRestaurantId(id) {
		updateMapping(
			OfferMappingField.RESTAURANT_IDS,
			addMappingId(mapping[OfferMappingField.RESTAURANT_IDS], id)
		);
		setRestaurantModalOpen(false);
	}

	function handleAddUserId(id) {
		updateMapping(
			OfferMappingField.USER_IDS,
			addMappingId(mapping[OfferMappingField.USER_IDS], id)
		);
		setUserModalOpen(false);
	}

	return (
		<TwoColumnLayout>
			<Section>
				<SectionTitle>Offer Mapping</SectionTitle>

				<Field id="offer-mapping-menu-ids-field">
					<LabelRow>
						<FieldLabel as="span">Menus</FieldLabel>
						<ApplyAllLabel htmlFor="offer-mapping-apply-all-menus">
							<CompactCheckbox
								id="offer-mapping-apply-all-menus"
								checked={applyAllMenus}
								onChange={(e) =>
									handleApplyAll(OfferMappingField.MENU_IDS, e.target.checked, true)
								}
							/>
							Apply to all
						</ApplyAllLabel>
					</LabelRow>
					{!applyAllMenus && (
						<>
							<Modal
								isOpen={menuModalOpen}
								onClose={() => setMenuModalOpen(false)}
								title="Search Menus">
								<MenuSearchModal selectedIds={menuIds} onSelect={handleSelectMenu} />
							</Modal>
							<TagList>
								{menuIds.map((id) => (
									<Tag key={id}>
										{menuLabels[id] || id}
										<IconButton
											$danger
											role="button"
											aria-label={`Remove ${menuLabels[id] || id}`}
											onClick={() => removeMenuId(id)}>
											delete
										</IconButton>
									</Tag>
								))}
								<IconButton
									role="button"
									aria-label="Add menu"
									onClick={() => setMenuModalOpen(true)}>
									add
								</IconButton>
							</TagList>
						</>
					)}
				</Field>

				<Field id="offer-mapping-restaurant-ids-field">
					<LabelRow>
						<FieldLabel as="span">Restaurant IDs</FieldLabel>
						<ApplyAllLabel htmlFor="offer-mapping-apply-all-restaurants">
							<CompactCheckbox
								id="offer-mapping-apply-all-restaurants"
								checked={applyAllRestaurants}
								onChange={(e) =>
									handleApplyAll(OfferMappingField.RESTAURANT_IDS, e.target.checked)
								}
							/>
							Apply to all
						</ApplyAllLabel>
					</LabelRow>
					{!applyAllRestaurants && (
						<>
							<Modal
								isOpen={restaurantModalOpen}
								onClose={() => setRestaurantModalOpen(false)}
								title="Add Restaurant">
								<MachineSelectModal
									isOpen={restaurantModalOpen}
									selectedIds={restaurantIds}
									onAdd={handleAddRestaurantId}
								/>
							</Modal>
							<TagList>
								{restaurantIds.map((id) => (
									<Tag key={id}>
										{id}
										<IconButton
											$danger
											role="button"
											aria-label={`Remove restaurant ${id}`}
											onClick={() =>
												removeId(OfferMappingField.RESTAURANT_IDS, id)
											}>
											delete
										</IconButton>
									</Tag>
								))}
								<IconButton
									role="button"
									aria-label="Add restaurant ID"
									onClick={() => setRestaurantModalOpen(true)}>
									add
								</IconButton>
							</TagList>
						</>
					)}
				</Field>

				<Field id="offer-mapping-user-ids-field">
					<LabelRow>
						<FieldLabel as="span">User IDs</FieldLabel>
						<ApplyAllLabel htmlFor="offer-mapping-apply-all-users">
							<CompactCheckbox
								id="offer-mapping-apply-all-users"
								checked={applyAllUsers}
								onChange={(e) =>
									handleApplyAll(OfferMappingField.USER_IDS, e.target.checked)
								}
							/>
							Apply to all
						</ApplyAllLabel>
					</LabelRow>
					{!applyAllUsers && (
						<>
							<Modal
								isOpen={userModalOpen}
								onClose={() => setUserModalOpen(false)}
								title="Add User ID">
								<IdEntryModal
									placeholder="Enter user ID"
									selectedIds={userIds}
									onAdd={handleAddUserId}
								/>
							</Modal>
							<TagList>
								{userIds.map((id) => (
									<Tag key={id}>
										{id}
										<IconButton
											$danger
											role="button"
											aria-label={`Remove user ${id}`}
											onClick={() => removeId(OfferMappingField.USER_IDS, id)}>
											delete
										</IconButton>
									</Tag>
								))}
								<IconButton
									role="button"
									aria-label="Add user ID"
									onClick={() => setUserModalOpen(true)}>
									add
								</IconButton>
							</TagList>
						</>
					)}
				</Field>
			</Section>
		</TwoColumnLayout>
	);
};

export default MappingAndTermsStep;
